import { useRef } from 'react'

import styles from './Tabs.module.scss'

interface TabsProps {
  tabs: string[]
  activeTab: number
  setActiveTab: (tab: number) => void
  disabled?: boolean
}

const Tabs = ({ tabs, activeTab, setActiveTab: onSelect, disabled = false }: TabsProps) => {
  const tabRefs = useRef<(HTMLDivElement | null)[]>([])

  const setActiveTab = (tab: number) => {
    if (!disabled) onSelect(tab)
  }

  const focusAndSelect = (index: number) => {
    if (disabled) return
    const wrapped = (index + tabs.length) % tabs.length
    tabRefs.current[wrapped]?.focus()
    setActiveTab(wrapped)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, index: number) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault()
      focusAndSelect(index + 1)
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault()
      focusAndSelect(index - 1)
    } else if (event.key === 'Home') {
      event.preventDefault()
      focusAndSelect(0)
    } else if (event.key === 'End') {
      event.preventDefault()
      focusAndSelect(tabs.length - 1)
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      setActiveTab(index)
    }
  }

  return (
    <div className={styles.tabContainer} role="tablist">
      {tabs.map((tab, index) => (
        <div
          key={index}
          ref={(el) => {
            tabRefs.current[index] = el
          }}
          className={`${styles.tab} ${activeTab === index ? styles.active : ''} ${disabled ? styles.disabled : ''}`}
          role="tab"
          aria-selected={activeTab === index}
          aria-disabled={disabled || undefined}
          tabIndex={activeTab === index ? 0 : -1}
          onClick={() => setActiveTab(index)}
          onKeyDown={(event) => handleKeyDown(event, index)}
        >
          <div className={styles.tabInner}>{tab}</div>
        </div>
      ))}
    </div>
  )
}

export default Tabs
